/**
 * The two spellings a run and its text are written in, and the mapping between
 * them.
 *
 * Ordinary paragraph text is `w:r` holding `w:t`; the text of an equation is
 * `m:r` holding `m:t`, in the math namespace. The engine treats both as runs of
 * characters — the paragraph model already reads `m:t` as text — so every place
 * that builds a run, a text node or a revision has to build it in the spelling
 * of the run it is working on.
 *
 * WHERE THE TWO DIFFER, as Word writes them
 *   ordinary deletion  <w:del …><w:r><w:delText>the words</w:delText></w:r></w:del>
 *   math deletion      <m:r><w:del …><w:rPr…/><m:t>the words</m:t></w:del></m:r>
 *   ordinary insertion <w:ins …><w:r><w:t>the words</w:t></w:r></w:ins>
 *   math insertion     <m:r><w:ins …><w:rPr…/><m:t>the words</m:t></w:ins></m:r>
 *
 * So in math the revision container sits INSIDE the run, the run properties go
 * inside the container with the text, and deleted text stays `m:t` — there is
 * no math spelling of `w:delText`. The containers themselves stay
 * wordprocessing elements in both.
 */
import { elementNameWithoutPrefix, WORDPROCESSINGML_NS } from "./ooxml-names";

/** The math namespace, the one an equation's runs and text nodes are written in. */
export const MATH_NS = "http://schemas.openxmlformats.org/officeDocument/2006/math";

export class RunSpelling {
    /**
     * Whether the element is written in the math spelling.
     *
     * FALLBACK
     *   - namespace known      -> the math namespace says it
     *   - namespace missing    -> the `m:` prefix on the name says it, which is what a
     *                             paragraph parsed on its own carries, having lost the
     *                             document's namespace declarations
     */
    static isMath(element: Element): boolean {
        return element.namespaceURI === MATH_NS || element.nodeName.startsWith("m:");
    }

    /**
     * A new element named `localName` in the same spelling as `nearby`, so a
     * text node built beside an equation's run is `m:t` and one built beside an
     * ordinary run is `w:t`.
     *
     * EXAMPLE
     *   nearby = <m:r>, localName = "r"  -> <m:r>
     *   nearby = <m:r>, localName = "t"  -> <m:t>
     *   nearby = <w:r>, localName = "t"  -> <w:t>
     */
    static elementLike(nearby: Element, localName: string): Element {
        const ownerDocument = nearby.ownerDocument as Document;
        if (RunSpelling.isMath(nearby)) return ownerDocument.createElementNS(MATH_NS, `m:${localName}`);
        return ownerDocument.createElementNS(WORDPROCESSINGML_NS, `w:${localName}`);
    }

    /**
     * The run properties of a run in either spelling: a math run carries its
     * character formatting in a `w:rPr` and its math styling in an `m:rPr`, and
     * a copy of the run needs both.
     */
    static runPropertiesOf(run: Element): Element[] {
        return Array.from(run.children).filter((child) => elementNameWithoutPrefix(child) === "rPr");
    }
}
